import type { Ring, Vec2 } from '../../geo/types';
import type { WaterEdge } from '../../site/types';
import { pointInRing } from '../../geo/polygon';
import type { LotSource } from './types';

/**
 * PLAN Lots: the IJ front of the city. Houses stood with their fronts to the harbour behind a bank,
 * as the painting shows along the whole waterfront and Van Deventer along the Nieuwendijk and the
 * Zeedijk. `bank` is the strip between the water and the house fronts, as wide as the city's quays;
 * `probe` looks that far inland from an edge to tell whether the shore bounds the walled city, since
 * the IJ edge runs up to 2 m outside the enclosure's line in places; `reach` is how far from the
 * shore the features that mark a quay's ends may stand.
 */
export const SHORE = { bank: 5, minLength: 20, join: 0.05, probe: 3, reach: 60 } as const;

/**
 * PLAN Lots, the IJ quay (round 6, 2026-09-27). From St Olof's chapel to the Kamperhoofd the houses
 * stood behind a quay about 10 m wide: Van Deventer draws a street band that wide along the IJ
 * there, dotted on its landward side only (c. 1560); the painting shows a raised band walled at the
 * water, the Kamperhoofd's guns at its east end (1538); the woodcut a wharf stacked with timber and
 * barrels (1544). The narrow strips at the Nieuwe Brug and along the Haringpakkerij keep the bank.
 * `from` and `to` name the layout features that mark its ends: the chapel, 40 m inland, and the
 * Schreierstoren, beside which the Kamperhoofd stood.
 */
export const IJ_QUAY = {
  width: 10,
  from: 'landmark-sint-olofskapel',
  to: 'tower-schreierstoren',
} as const;

export interface ShoreQuay {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly width: number;
}

interface Placed {
  readonly id: string;
  readonly pos: Vec2;
}

export interface QuayMarks {
  readonly landmarks: readonly Placed[];
  readonly fortifications: { readonly towers: readonly Placed[] };
}

export function ijQuays(marks: QuayMarks): readonly ShoreQuay[] {
  const at = (id: string): Vec2 => {
    const feature = [...marks.landmarks, ...marks.fortifications.towers].find((f) => f.id === id);
    if (!feature) throw new Error(`IJ quay: the layout has no ${id}`);
    return [feature.pos[0], feature.pos[1]];
  };
  return [{ from: at(IJ_QUAY.from), to: at(IJ_QUAY.to), width: IJ_QUAY.width }];
}

/* Squares and the shore come after every street, quay and alley: a house faces them only where no street took the ground. */
const FRONT_PRIORITY = 5;

function inland(edge: WaterEdge): Vec2 {
  const { a, b } = edge.segment;
  return [
    (a[0] + b[0]) / 2 - edge.towardWater[0] * SHORE.probe,
    (a[1] + b[1]) / 2 - edge.towardWater[1] * SHORE.probe,
  ];
}

function lineLength(line: readonly Vec2[]): number {
  let total = 0;
  for (let i = 1; i < line.length; i++) {
    total += Math.hypot(line[i]![0] - line[i - 1]![0], line[i]![1] - line[i - 1]![1]);
  }
  return total;
}

/* Edges come ring by ring in order, so a run of IJ edges that meet end to end is one stretch of shore. */
function chains(edges: readonly WaterEdge[]): readonly (readonly Vec2[])[] {
  return edges.reduce<(readonly Vec2[])[]>((out, edge) => {
    const last = out[out.length - 1];
    const tail = last?.[last.length - 1];
    const joins =
      tail !== undefined &&
      Math.hypot(tail[0] - edge.segment.a[0], tail[1] - edge.segment.a[1]) <= SHORE.join;
    return joins
      ? [...out.slice(0, -1), [...last!, edge.segment.b]]
      : [...out, [edge.segment.a, edge.segment.b]];
  }, []);
}

function arcOf(line: readonly Vec2[], p: Vec2): { readonly s: number; readonly distance: number } {
  let best = { s: 0, distance: Infinity };
  let start = 0;
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i]!;
    const b = line[i + 1]!;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const u: Vec2 = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
    const along = Math.min(Math.max((p[0] - a[0]) * u[0] + (p[1] - a[1]) * u[1], 0), length);
    const distance = Math.hypot(p[0] - a[0] - u[0] * along, p[1] - a[1] - u[1] * along);
    if (distance < best.distance) best = { s: start + along, distance };
    start += length;
  }
  return best;
}

function between(line: readonly Vec2[], from: number, to: number): readonly Vec2[] {
  const out: Vec2[] = [];
  const push = (p: Vec2) => {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 1e-9) out.push(p);
  };
  let start = 0;
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i]!;
    const b = line[i + 1]!;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const end = start + length;
    const at = (x: number): Vec2 => [
      a[0] + ((b[0] - a[0]) / length) * (x - start),
      a[1] + ((b[1] - a[1]) / length) * (x - start),
    ];
    if (out.length === 0 && from <= end) push(from <= start ? a : at(from));
    if (out.length > 0) {
      if (to <= end) {
        push(to >= end ? b : at(to));
        return out;
      }
      push(b);
    }
    start = end;
  }
  return out;
}

interface ShorePiece {
  readonly line: readonly Vec2[];
  readonly bank: number;
}

function cutAt(pieces: readonly ShorePiece[], quay: ShoreQuay): readonly ShorePiece[] {
  return pieces.flatMap((piece): readonly ShorePiece[] => {
    const ends = [arcOf(piece.line, quay.from), arcOf(piece.line, quay.to)];
    if (piece.bank !== SHORE.bank || ends.some((e) => e.distance > SHORE.reach)) return [piece];
    const [from, to] = ends.map((e) => e.s).sort((x, y) => x - y) as [number, number];
    return [
      { line: between(piece.line, 0, from), bank: SHORE.bank },
      { line: between(piece.line, from, to), bank: quay.width },
      { line: between(piece.line, to, lineLength(piece.line)), bank: SHORE.bank },
    ];
  });
}

/** One source per stretch of the IJ shore that bounds the walled city, named after where it starts. */
export function collectShoreSources(
  edges: readonly WaterEdge[],
  enclosure: Ring,
  quays: readonly ShoreQuay[] = []
): readonly LotSource[] {
  const city = edges.filter(
    (edge) => edge.waterKind === 'ij' && pointInRing(inland(edge), enclosure)
  );
  return chains(city)
    .flatMap((line) => quays.reduce(cutAt, [{ line, bank: SHORE.bank }]))
    .filter(({ line }) => line.length >= 2 && lineLength(line) >= SHORE.minLength)
    .map(({ line, bank }) => ({
      id: `shore:${line[0]![0].toFixed(1)},${line[0]![1].toFixed(1)}`,
      kind: 'shore',
      centerline: line,
      widths: line.map(() => 2 * bank),
      priority: FRONT_PRIORITY,
    }));
}

/**
 * Every square's outline, all round and of no width, so houses face it from the ground around it:
 * the Dam's fronts stood on the square itself (the Oude Stadhuis and the houses beside it).
 */
export function collectSquareSources(
  squares: readonly { readonly id: string; readonly polygon: readonly Vec2[] }[]
): readonly LotSource[] {
  return squares
    .filter((square) => square.polygon.length >= 3)
    .map((square) => {
      const line = [...square.polygon, square.polygon[0]!];
      return {
        id: square.id,
        kind: 'square',
        centerline: line,
        widths: line.map(() => 0),
        priority: FRONT_PRIORITY,
      };
    });
}
